// "All agents": the project's agents in one table — who uses each (its apps and workflow steps), its tests and how
// the last run of them did, when it last changed — with what needs a look on top (no tests, failing tests) and
// every agent's tests run in one click. A row opens the agent. The file-and-line index (Where used) and every
// test in one list (Evaluations) stay one click away.
import { AppWindow, FileCode2, FlaskConical, LayoutGrid, Loader2, Play } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { runModel, shortModel, type EvalRun, useEvalsKey } from './Evaluations.tsx';
import { useAgentsHost, useAgentsQuery, useInvalidate } from './host.tsx';
import { AgentIcon } from './AgentIcon.tsx';
import { useAgentCatalog, useAgentUsage } from './catalog.tsx';
import { ago, type AgentSummary } from './model.ts';
import { btnGhost, messageOf } from './ui.tsx';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** When, as people say it: `24 min ago`, `3 days ago`. */
const when = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days >= 1 ? plural(days, 'day') + ' ago' : ago(iso);
};

function Kpi({
  n,
  label,
  tone,
  testId,
}: {
  n: number;
  label: string;
  tone?: 'warn' | 'bad';
  testId: string;
}) {
  return (
    <div
      className="rounded-md border border-card-border bg-card-bg px-3 py-2 text-xs text-page-muted"
      data-testid={testId}
    >
      <b
        className={
          'block text-lg ' +
          (tone === 'bad' && n ? 'text-red-600' : tone === 'warn' && n ? 'text-amber-600' : 'text-page-title')
        }
      >
        {n}
      </b>
      {label}
    </div>
  );
}

/** How an agent's tests stand: they pass, one fails, it has none, they never ran, or a run is running. */
export type TestStatus = 'pass' | 'fail' | 'none' | 'never' | 'running';

/**
 * The app's agent tests and the last run of each agent's (one query each, shared by the list and All agents):
 * the enabled cases of a subject, its last run, and how its tests stand.
 */
export function useAgentTests() {
  const port = useAgentsHost().evals;
  const ek = useEvalsKey();
  const suites = useAgentsQuery(ek('suites'), () => port!.suites(), {
    enabled: !!port,
  });
  const cases = (subject: string) =>
    (suites.data ?? [])
      .filter((s) => s.subject === subject)
      .flatMap((s) => s.cases.filter((c) => c.enabled !== false));
  const tested = [...new Set((suites.data ?? []).map((s) => s.subject))]
    .filter((s) => cases(s).length)
    .sort();
  const last = useAgentsQuery(
    ek('last-runs', tested.join(',')),
    async () => {
      const out: Record<string, EvalRun | null> = {};
      for (const s of tested) out[s] = (await port!.runs({ subject: s }))[0] ?? null;
      return out;
    },
    { enabled: !!port && !!tested.length },
  );
  const status = (subject: string): TestStatus | null => {
    if (!port || !suites.data) return null;
    if (!cases(subject).length) return 'none';
    const r = last.data?.[subject];
    if (!r) return last.data ? 'never' : null;
    if (r.status === 'running') return 'running';
    return r.passed < r.total ? 'fail' : 'pass';
  };
  return { port, cases, tested, last, status };
}

export function AllAgentsPanel({
  agents,
  openUsage,
  openEvals,
}: {
  /** The project agents as the list has them (when each changed). */
  agents: AgentSummary[];
  openUsage(): void;
  openEvals: (() => void) | null;
}) {
  const host = useAgentsHost();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const ek = useEvalsKey();
  const catalog = useAgentCatalog();
  const usage = useAgentUsage();
  const { port, cases, tested, last } = useAgentTests();
  const docs = (catalog.data?.agents ?? []).filter((d) => d.kind === 'project');
  const [running, setRunning] = React.useState(false);
  const [err, setErr] = React.useState('');

  const runAll = async () => {
    if (!port) return;
    setRunning(true);
    setErr('');
    try {
      for (const s of tested) await port.start({ keys: cases(s).map((c) => c.key) });
      invalidate([ek()]);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setRunning(false);
    }
  };

  if ((catalog.loading && !catalog.data) || (usage.loading && !usage.data))
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-page-muted">
        <Loader2 size={14} className="animate-spin" /> Reading the project’s agents…
      </div>
    );

  const sites = usage.data?.sites ?? [];
  const rows = docs.map((d) => {
    const own = sites.filter((s) => s.subject === d.subject);
    // A workflow step counts once: its declaration, not every saved version of the workflow.
    const steps = own.filter((s) => s.kind === 'workflow' && s.file.includes('/declarations/')).length;
    const apps = (usage.data?.apps ?? []).filter((a) => a.subjects.includes(d.subject)).map((a) => a.app);
    const n = cases(d.subject).length;
    const run = last.data?.[d.subject] ?? null;
    return { d, apps, steps, n, run, changed: agents.find((a) => a.key === d.key)?.updatedAt ?? null };
  });
  const failing = rows.filter(
    (r) => r.run && r.run.status !== 'running' && r.run.passed < r.run.total,
  ).length;

  return (
    <div className="p-6 max-w-6xl" data-testid="agents-all-panel">
      <h1 className="text-xl font-semibold text-page-title m-0 flex items-center gap-2">
        <LayoutGrid size={18} /> All agents of this project
      </h1>
      <p className="text-sm text-page-muted mt-1 mb-3">
        Where each agent is used and how its tests did. Click a row to open the agent.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <Kpi n={rows.length} label="agents" testId="agents-all-count" />
        <Kpi n={rows.filter((r) => r.apps.length).length} label="used by apps" testId="agents-all-used" />
        {port ? (
          <>
            <Kpi
              n={rows.filter((r) => !r.n).length}
              label="without tests"
              tone="warn"
              testId="agents-all-untested"
            />
            <Kpi n={failing} label="failing tests" tone="bad" testId="agents-all-failing" />
          </>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-md border border-card-border bg-card-bg">
        <table className="w-full text-sm border-collapse" data-testid="agents-all-table">
          <thead>
            <tr className="text-left text-xs text-page-muted">
              <th className="py-2 px-3 font-medium">Agent</th>
              <th className="py-2 px-3 font-medium">Used by</th>
              {port ? (
                <>
                  <th className="py-2 px-3 font-medium">Tests</th>
                  <th className="py-2 px-3 font-medium">Last test run</th>
                </>
              ) : null}
              <th className="py-2 px-3 font-medium">Changed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.d.key}
                className="border-t border-divider align-top cursor-pointer hover:bg-page-bg"
                data-testid={`agents-all-row-${r.d.key}`}
                onClick={() => navigate(host.paths.agent(r.d.key))}
              >
                <td className="py-2 px-3">
                  <span className="inline-flex items-center gap-1.5 font-medium text-page-title">
                    <AgentIcon icon={r.d.icon} /> {r.d.title}
                  </span>
                </td>
                <td className="py-2 px-3">
                  {r.apps.map((a) => (
                    <span
                      key={a}
                      className="mr-1 mb-1 inline-flex items-center gap-1 rounded-full border border-card-border px-2 text-xs"
                    >
                      <AppWindow size={11} /> {a}
                    </span>
                  ))}
                  {r.steps ? (
                    <div className="text-xs text-page-muted">{plural(r.steps, 'workflow step')}</div>
                  ) : null}
                  {!r.apps.length && !r.steps ? <span className="text-page-muted">—</span> : null}
                </td>
                {port ? (
                  <>
                    <td className="py-2 px-3">{r.n || <span className="text-page-muted">—</span>}</td>
                    <td className="py-2 px-3 text-xs" data-testid={`agents-all-last-${r.d.key}`}>
                      {!r.n ? (
                        <span className="text-amber-600">● no tests</span>
                      ) : !r.run ? (
                        <span className="text-page-muted">{last.loading ? '…' : 'never run'}</span>
                      ) : r.run.status === 'running' ? (
                        <span className="text-page-muted">running…</span>
                      ) : (
                        <>
                          <span className={r.run.passed < r.run.total ? 'text-red-600' : 'text-green-600'}>
                            {r.run.passed < r.run.total ? '✗' : '✓'} {r.run.passed}/{r.run.total}
                          </span>
                          <span className="text-page-muted">
                            {` · ${shortModel(runModel(r.run.params))} · ${when(r.run.startedAt)}`}
                          </span>
                        </>
                      )}
                    </td>
                  </>
                ) : null}
                <td className="py-2 px-3 text-xs text-page-muted whitespace-nowrap">{when(r.changed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {port && tested.length ? (
          <>
            <button
              type="button"
              className={btnGhost}
              data-testid="agents-all-run"
              disabled={running}
              onClick={() => void runAll()}
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run all tests
            </button>
            <span className="text-xs text-page-muted">
              runs every agent’s tests with its own model ({plural(tested.length, 'agent')})
            </span>
          </>
        ) : null}
        <span className="ml-auto flex gap-3 text-xs">
          {openEvals ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-nav-active cursor-pointer"
              data-testid="agents-evals"
              onClick={openEvals}
            >
              <FlaskConical size={12} /> Every test in one list ›
            </button>
          ) : null}
          <button
            type="button"
            className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-nav-active cursor-pointer"
            data-testid="agents-usage"
            onClick={openUsage}
          >
            <FileCode2 size={12} /> Show every place in the code ›
          </button>
        </span>
      </div>
      {err ? <div className="mt-2 text-sm text-red-600">{err}</div> : null}
    </div>
  );
}
